import { type Session, type AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../clients/supabaseClient';
import type { Profile } from '../types';

// ── Session ────────────────────────────────────────────────────────────────────

// Returns the current session, or null if the user is not signed in.
export async function getSessionFromDb(): Promise<Session | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
}

// Attempts to refresh the current session token.
// Logs a warning if the refresh fails — the caller decides how to handle it.
export async function refreshSessionInDb(): Promise<void> {
    const { error } = await supabase.auth.refreshSession();
    if (error) console.warn('Session refresh failed:', error.message);
}

// Subscribes to auth state changes (sign in, sign out, token refresh, etc.).
// Returns the unsubscribe function to be called on cleanup.
export function subscribeToAuthChanges(
    callback: (event: AuthChangeEvent, session: Session | null) => void
): () => void {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
    return () => subscription.unsubscribe();
}

// ── Profile ────────────────────────────────────────────────────────────────────

// Fetches the admin profile row for a given auth user ID.
// Returns null if the profile doesn't exist or the query fails — non-fatal.
export async function fetchProfileFromDb(userId: string): Promise<Profile | null> {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
        if (error) return null;
        return data as Profile;
    } catch {
        return null;
    }
}

// ── Auth actions ───────────────────────────────────────────────────────────────

// Signs the user in with email and password. Throws if credentials are invalid
// or Supabase returns an error.
export async function signInWithPasswordInDb(email: string, password: string): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
}

// Signs the current user out and clears their session. Throws on error.
export async function signOutFromDb(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
}

// Sends a password reset email to the given address. The redirectTo URL is
// read from the VITE_APP_URL env variable so it works across staging and
// production without hardcoding. Throws if Supabase returns an error.
export async function sendPasswordResetEmailInDb(email: string): Promise<void> {
    const redirectTo = `${import.meta.env.VITE_APP_URL ?? ''}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
}

// Updates the current user's password. Called after the user has arrived via
// a password reset link and entered their new password. Throws on error.
export async function updateUserPasswordInDb(password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
}

// ── Auto-refresh helpers ───────────────────────────────────────────────────────

// Starts the Supabase token auto-refresh timer.
// Called when the window regains focus so tokens don't expire while the tab is active.
export function startAuthAutoRefresh(): void {
    try { supabase.auth.startAutoRefresh?.(); } catch { /* non-fatal */ }
}

// Stops the Supabase token auto-refresh timer.
// Called when the window loses focus to avoid unnecessary background refreshes.
export function stopAuthAutoRefresh(): void {
    try { supabase.auth.stopAutoRefresh?.(); } catch { /* non-fatal */ }
}